import { useState } from "react";
import { Form, Input, Button, Switch, notification } from "antd";
import { AiOutlineMail, AiOutlineKey } from "react-icons/ai";
import { SiLastpass } from "react-icons/si";
import { Link } from "react-router-dom";
import ChanelsLogo from "../../../assets/svg/chanels-logo.svg";

import BGImg from "../../../assets/png/v300batch2-nunoon-13.jpg";
import {
  useForgotPasswordMutation,
  useValidateResetCodeMutation,
} from "../../../services/auth.service";
import { isWWWhostname } from "../../../utils/helpers";

const ForgotPassword = () => {
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [forgotPassword] = useForgotPasswordMutation();
  const [validateResetCode] = useValidateResetCodeMutation();

  const openNotification = (type, message) => {
    notification[type]({
      message: type === "success" ? "Success" : "Error",
      description: message,
      placement: "topRight",
    });
  };

  const onCodeRequest = async (values) => {
    try {
      setLoading(true);
      const response = await forgotPassword({
        email: values.email,
        subdomain: isWWWhostname() === "xyz" ? "xyz" : isWWWhostname(), // Using the same subdomain as seen in other components
      }).unwrap();

      setLoading(false);

      if (response.response === "success") {
        openNotification(
          "success",
          "Your reset code has been sent to the provided account."
        );
        setSent(true);
      } else {
        openNotification(
          "error",
          response.message || "Failed to send the code. Please try again."
        );
      }
    } catch (error) {
      setLoading(false);
      openNotification(
        "error",
        error.data?.message || "Failed to send the code. Please try again."
      );
    }
  };

  const onChangePassword = async (values) => {
    try {
      setLoading(true);
      const response = await validateResetCode({
        email: values.email,
        password: values.code, // The reset code sent to email
        newpassword: values.password, // The new password
        subdomain: isWWWhostname() === "xyz" ? "xyz" : isWWWhostname(), // Using the same subdomain as seen in other components
      }).unwrap();

      setLoading(false);

      if (response.response === "success") {
        openNotification(
          "success",
          "Password has been reset successfully. You can now login with your new password."
        );
        // Redirect to login page after successful password reset
        setTimeout(() => {
          window.location.href = "/";
        }, 2000);
      } else {
        openNotification(
          "error",
          response.message || "Failed to reset password. Please try again."
        );
      }
    } catch (error) {
      setLoading(false);
      openNotification(
        "error",
        error.data?.message || "Failed to reset password. Please try again."
      );
    }
  };

  return (
    <div className="h-screen w-screen flex items-center justify-center">
      <div
        className="h-screen absolute top-0 left-0 w-screen"
        style={{
          background: `url(${BGImg})`,
          backgroundPosition: "center",
          backgroundRepeat: "center",
          backgroundSize: "cover",
          opacity: 0.1,
          backgroundAttachment: "fixed",
        }}
      ></div>
      <div className="bg-white w-[95%] md:w-[480px] drop-shadow-md rounded-md p-6 relative z-10">
        <div className="flex flex-col items-center justify-center">
          <div className="flex w-24 h-24 overflow-hidden">
            <img
              src={ChanelsLogo}
              alt="Chanels Logo"
              className="h-full scale-150 w-full object-cover"
            />
          </div>
        </div>

        <div className="text-center font-semibold text-[18px] text-gray-900">
          {sent ? "Update Password" : "Chanels Password Recovery"}
        </div>
        <hr className="my-2 border-gray-100" />
        <div className="text-center font-light mt-4 text-primary text-1xl pb-2">
          {sent
            ? "Enter the 4-digit code alongside your new password"
            : "A 4-digit code will be sent to your email."}
        </div>

        {sent ? (
          // Form for updating password
          <Form
            layout="vertical"
            className=""
            size="large"
            onFinish={onChangePassword}
          >
            <Form.Item
              name="email"
              label="Email"
              rules={[
                { required: true, message: "Please enter your email address." },
                {
                  type: "email",
                  message: "Please enter a valid email address.",
                },
              ]}
            >
              <Input
                size="large"
                prefix={<AiOutlineMail className="text-gray-500" />}
                placeholder="Enter your email address"
              />
            </Form.Item>

            <Form.Item
              name="code"
              label="Code"
              rules={[
                { required: true, message: "Please enter the 4-digit code." },
              ]}
            >
              <Input
                prefix={<SiLastpass className="text-primary" />}
                placeholder="Enter your 4-digit code"
                type="number"
              />
            </Form.Item>

            <Form.Item
              name="password"
              label="New Password"
              rules={[
                { required: true, message: "Please enter your new password." },
              ]}
            >
              <Input.Password
                prefix={<AiOutlineKey className="text-primary" />}
                placeholder="Enter your new password"
              />
            </Form.Item>

            <Form.Item name="logoutAll" valuePropName="checked">
              <div className="flex justify-between">
                <span className="text-primary font-normal text-sm">
                  Logout from all devices
                </span>
                <Switch />
              </div>
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                className="!bg-black "
                loading={loading}
                block
              >
                Continue
              </Button>
            </Form.Item>
          </Form>
        ) : (
          // Form for requesting password reset code
          <Form
            layout="vertical"
            className="mt-4"
            size="large"
            onFinish={onCodeRequest}
          >
            <Form.Item
              name="email"
              rules={[
                { required: true, message: "Please enter your email address." },
                {
                  type: "email",
                  message: "Please enter a valid email address.",
                },
              ]}
            >
              <Input
                prefix={<AiOutlineMail className="text-primary" />}
                placeholder="Enter your email address"
              />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                className="!text-white !bg-primary"
                htmlType="submit"
                loading={loading}
                block
              >
                Send Code
              </Button>
            </Form.Item>
          </Form>
        )}

        <div className="text-center mt-4">
          <Link to="/" className="underline text-secondary">
            Already have an account? Log in.
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
